import { useState, useCallback, useMemo, useEffect } from 'react';
import {
  AppState,
  AnalysisResult,
  RecordingMetadata,
  Biomechanics,
  SeverityMatrix,
  YouTubeReference
} from '../../types';

import { recomputeScoreWithNewLevel } from '../../services/scoreService';
import { cleanupAllMemory } from '../../services/memoryService';
import {
  clearLastAnalysisRawPayload,
  getLastAnalysisRawPayload,
  getLastHarmonyOverride
} from '../../services/analysisCache';

import {
  FACTORY_SEVERITY_MATRIX,
  updateSeverityMatrix as syncSeverityEngine
} from '../../services/severityEngine';

import { getAICoachingAdvice } from '../../services/aiCoachingService';
import { generateAllCoachingAdvice } from '../../services/CoachSeverityEngine';
import { DETERMINISM_POLICY } from '../../contracts/determinism';
import { sealResult } from '../../policy/integrityProtocol';

// ✅ NEW: truth + narrative
import { buildTruthPayload } from '../../services/truth/buildTruthPayload';

export function useAnalysisCore(
  setCoachTransitionLabel: (l: string | null) => void,
  setIsCoachTransition: (b: boolean) => void,
  setAtomicOffset: (n: number) => void
) {
  const [appState, setAppState] = useState<AppState>(AppState.IDLE);
  const [refFile, setRefFile] = useState<File | null>(null);
  const [youtubeMasterRef, setYoutubeMasterRef] = useState<YouTubeReference | null>(null);
  const [studentFile, setStudentFile] = useState<File | null>(null);
  const [studentMetadata, setStudentMetadata] = useState<RecordingMetadata | null>(null);
  const [result, setResult] = useState<AnalysisResult | null>(null);

  const [currentLevelId, setCurrentLevelId] = useState<number>(() => {
    try {
      const saved = localStorage.getItem('gsc_active_level');
      return saved ? parseInt(saved, 10) : 4;
    } catch {
      return 4;
    }
  });

  const [severityMatrix, setSeverityMatrix] = useState<SeverityMatrix>(() => {
    const saved = localStorage.getItem('gsc_god_mode_matrix');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {}
    }
    return { ...FACTORY_SEVERITY_MATRIX };
  });

  const [audioQualityWarning, setAudioQualityWarning] = useState<string | null>(null);

  useEffect(() => {
    localStorage.setItem('gsc_active_level', currentLevelId.toString());
  }, [currentLevelId]);

  const updateSeverity = useCallback((newMatrix: SeverityMatrix) => {
    setSeverityMatrix(newMatrix);
    syncSeverityEngine(newMatrix);
  }, []);

  const resetSeverityToFactory = useCallback(() => {
    const factory = { ...FACTORY_SEVERITY_MATRIX };
    setSeverityMatrix(factory);
    syncSeverityEngine(factory);
  }, []);

  const handleSetStudentFile = useCallback((file: File | null, metadata?: RecordingMetadata) => {
    setStudentFile(file);
    setStudentMetadata(metadata ?? null);
  }, []);

  const resetAll = useCallback(async () => {
    await cleanupAllMemory();

    const sessionId = result?.audit_trail?.scoring_ruleset_id?.replace('GSC_SESSION_', '');
    if (sessionId) {
      clearLastAnalysisRawPayload(sessionId);
    }

    setAppState(AppState.IDLE);
    setResult(null);
    setRefFile(null);
    setYoutubeMasterRef(null);
    setStudentFile(null);
    setStudentMetadata(null);
    setAudioQualityWarning(null);
    setAtomicOffset(0);
  }, [result, setAtomicOffset]);

  const updateBiomechanics = useCallback((biomech: Biomechanics | null) => {
    setResult(prev => {
      if (!prev) return null;
      return {
        ...prev,
        biomechanics: biomech,
        session: {
          ...prev.session,
          metrics: {
            ...prev.session.metrics,
            posture: biomech ? biomech.score : null
          }
        }
      };
    });
  }, []);

  const renormalize = useCallback(
    async (targetLevelId: number) => {
      if (!result) return;

      setIsCoachTransition(true);
      setCoachTransitionLabel(`RECALIBRAGE NEURAL L${targetLevelId}...`);
      setCurrentLevelId(targetLevelId);

      const startTime = Date.now();
      await new Promise(r => setTimeout(r, 150));

      try {
        const sessionId = result.audit_trail?.scoring_ruleset_id?.replace('GSC_SESSION_', '');
        if (!sessionId) {
          setIsCoachTransition(false);
          return;
        }

        const rawDsp = getLastAnalysisRawPayload(sessionId);
        if (!rawDsp) {
          setIsCoachTransition(false);
          return;
        }

        const isPremium = localStorage.getItem('gsc_is_premium') === 'true';
        const analysisCount = parseInt(localStorage.getItem('gsc_analysis_count') || '0', 10);

        const harmonyOverride = getLastHarmonyOverride(sessionId);

        const report = recomputeScoreWithNewLevel(
          rawDsp,
          targetLevelId,
          harmonyOverride ?? undefined,
          { isPremium, analysisCount }
        );

        const updatedBiomech: Biomechanics | null = result.biomechanics
          ? {
              ...result.biomechanics,
              score: report.metrics.posture || 0,
              humanFeedback: (report.metrics.posture || 0) > 80 ? 'Geste stable.' : 'Instabilité détectée.'
            }
          : null;

        const nextResultStub: AnalysisResult = {
          ...result,
          coaching_level: targetLevelId,
          session: {
            ...result.session,
            score: report.score,
            match_score: report.score,
            metrics: report.metrics
          },
          biomechanics: updatedBiomech,
          audit_trail: {
            ...result.audit_trail,
            computed_at: new Date().toISOString()
          }
        };

        const [aiBriefing, diagVerdict] = await Promise.all([
          getAICoachingAdvice(nextResultStub, 'fr', null, isPremium),
          generateAllCoachingAdvice(nextResultStub, targetLevelId, 'fr')
        ]);

        const finalResultData: AnalysisResult = {
          ...nextResultStub,
          session: {
            ...nextResultStub.session,
            rank:
              report.score !== null
                ? report.score >= 90
                  ? 'STUDIO MASTER'
                  : report.score >= 75
                  ? 'SESSION PLAYER'
                  : report.score >= 50
                  ? 'PRACTICING'
                  : 'BEGINNER'
                : nextResultStub.session.rank,
            truth_report: {
              ...nextResultStub.session.truth_report,
              status:
                DETERMINISM_POLICY.enforcement_level === 'hard' &&
                (nextResultStub.audit_trail as any).determinism_score?.isStable === false
                  ? 'INVALID'
                  : nextResultStub.session.truth_report.status
            }
          },

          /**
           * 🔒 EXTENSION PÉDAGOGIQUE — SANS AUCUNE PERTE
           */
          feedback: {
            ...nextResultStub.feedback,
            aiCoachSummary: aiBriefing.summary,
            diagnostics: diagVerdict.diagnostics,
            explanations: diagVerdict.explanations ?? [],
            primaryPrescription: diagVerdict.primaryPrescription
          }
        };

        // ✅ Seal result first (integrity protocol)
        let sealedResult = await sealResult(finalResultData);

        // ✅ Then attach TruthPayload + narrative (NO-SIM)
        try {
          const truth = await buildTruthPayload(sealedResult);
          sealedResult = {
            ...sealedResult,
            feedback: {
              ...sealedResult.feedback,
              narrative: truth.outputs,
              truth_payload: truth
            }
          };
        } catch (e) {
          // Truth must never break user flow
          console.warn('[TRUTH] buildTruthPayload failed:', e);
        }

        const elapsed = Date.now() - startTime;
        if (elapsed < 1800) {
          await new Promise(r => setTimeout(r, 1800 - elapsed));
        }

        setResult(sealedResult);
      } catch (e) {
        console.error('[RELEVEL] Drift error:', e);
      } finally {
        setTimeout(() => setIsCoachTransition(false), 200);
      }
    },
    [result, setCoachTransitionLabel, setIsCoachTransition]
  );

  return useMemo(
    () => ({
      appState,
      setAppState,
      refFile,
      setRefFile,
      youtubeMasterRef,
      setYoutubeMasterRef,
      studentFile,
      setStudentFile: handleSetStudentFile,
      studentMetadata,
      result,
      setResult,
      currentLevelId,
      setCurrentLevelId,
      severityMatrix,
      updateSeverity,
      resetSeverityToFactory,
      setCoachLevel: renormalize,
      resetAll,
      renormalize,
      updateBiomechanics,
      audioQualityWarning,
      setAudioQualityWarning
    }),
    [
      appState,
      refFile,
      youtubeMasterRef,
      studentFile,
      handleSetStudentFile,
      studentMetadata,
      result,
      currentLevelId,
      severityMatrix,
      updateSeverity,
      resetSeverityToFactory,
      renormalize,
      resetAll,
      updateBiomechanics,
      audioQualityWarning
    ]
  );
}
